import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'
import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'
import useBootcampProductCardStats from '~/statistics/hooks/useBootcampProductCardStats'
import { AppLanguage } from '~/types/locale'
import { ButtonPrimary } from '~/components/Buttons'
import { BootcampProductAPIStat } from '~/redux/apis/user-stats-api'
import { Link } from '~/components'

import ROUTES from '~/shared/constants/routes.const'

import TimeStat from '../../ProfileCards/TimeStat'
import PieProgress from '../PieProgress'

import ActivitySetsSummary from './ActivitySetsSummary'
import LevelStat from './LevelStat'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '28px' }]

export default function Details({ product }: { product: BootcampProductAPIStat }): React.ReactElement {
  const { t } = useTranslation('statistics')
  const router = useRouter()
  const isFamilyView = router.asPath.includes(ROUTES.FAMILY_ADMIN_PANEL)

  const {
    bossBattleUrl,
    activitySetsProgress,
    activitiesSummary: { totalSetsCount, setsCompletedCount, completedBossBattlesCount, totalBossBattlesCount },
    totalAnswered,
    videoProgress: { days, hours, minutes },
    video: { totalCount: totalVideoCount, watchedCount: watchedVideoCount },
    videoProgressBarData,
    levels,
    isBossBattleComplete,
    productHref,
    progressBarPercentage,
    id,
    supportsCurrentLocale,
  } = useBootcampProductCardStats(product)

  const locale = supportsCurrentLocale ? undefined : AppLanguage.EN

  const btnText = progressBarPercentage === 100 ? t('review') : t('continue')

  const onClickContinueBtn = (): void => {
    statisticsAnalytics.logButtonClick(btnText)
  }

  return (
    <div>
      <div className="flex space-x-4 my-4 pt-2">
        <ActivitySetsSummary
          setsCompletedCount={setsCompletedCount}
          setsProgress={activitySetsProgress}
          totalAnswered={totalAnswered}
          totalSetsCount={totalSetsCount}
        />
        <div className="w-full grow font-semibold rounded overflow-hidden shadow-card space-y-3 py-2">
          <p className="text-center">
            <span className="text-purple">{t('videos')}</span> {watchedVideoCount}/{totalVideoCount}
          </p>
          <PieProgress
            imgAltText={t('alt_texts.video_camera')}
            imgSrcKey="scoreboard/video-white.svg"
            progressBarData={videoProgressBarData}
            totalSlices={totalVideoCount}
          />
          <div className="flex items-center justify-center">
            <SVGImage
              alt={t('alt_texts.clock')}
              className="mr-2"
              height={20}
              srcKey="scoreboard/clock.svg"
              width={20}
            />
            <TimeStat days={days} hours={hours} minutes={minutes} />
          </div>
        </div>
      </div>
      <div className="relative">
        <div className="w-full pt-2">
          {Object.entries(levels).map(
            ([levelNo, { activitiesCount, activitySets, isCompleted, name }]) =>
              activitiesCount && (
                <LevelStat
                  activitySets={activitySets}
                  id={id}
                  isCompleted={isCompleted}
                  key={levelNo}
                  levelNo={parseInt(levelNo)}
                  locale={locale}
                  name={name}
                />
              ),
          )}
        </div>

        <div className="flex items-center justify-between font-medium space-x-2">
          <div className="flex items-center space-x-2">
            <ResponsiveImage
              alt={t('boss-battle')}
              imageWidths="28"
              mediaQuery={mediaQuery}
              srcKey="scoreboard/boss-avatar-v2.png"
              width={28}
            />
            <p>{t('boss-battle')}</p>
            {isBossBattleComplete ? (
              <SVGImage alt={t('alt_texts.completed')} srcKey="scoreboard/checkmark-v2.svg" width={20} />
            ) : (
              <p>
                {completedBossBattlesCount}/{totalBossBattlesCount}
              </p>
            )}
          </div>

          {!isFamilyView && (
            <Link locale={locale} to={bossBattleUrl}>
              <SVGImage alt={t('alt_texts.go')} srcKey="scoreboard/continue-icon.svg" />
            </Link>
          )}
        </div>

        {!isFamilyView && (
          <ButtonPrimary
            className="ml-auto mt-4 px-2"
            href={productHref}
            locale={locale}
            noPadding
            onClick={onClickContinueBtn}
            size="medium"
          >
            {btnText}
          </ButtonPrimary>
        )}
      </div>
    </div>
  )
}
